import * as React from "react";
import {
  Button,
  TextField,
  CircularProgress,
  Autocomplete,
  Grid,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Chip,
  keyframes,
} from "@mui/material";
import { useState, useEffect, useRef, useMemo } from "react";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import DI from "../../../utility/DI";
import dayjs from "dayjs";
import { FetchContext } from "../../SideBar";
import { environment } from "../../../../Env_data/environment";
import { categoryOptions } from "../../../../Constant";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

// Generate a unique batch token for this form session
const generateBatchToken = () =>
  "batch_" + Date.now() + "_" + Math.random().toString(36).substring(2, 9);

const HomeExpenseForm = (props) => {
  const { APP_NAME } = environment;
  const {
    handleClose,
    di: { POST, POST_FORM, urls, session, error, success },
  } = props;

  // Load last selected category from localStorage, fallback to default
  const getLastSelectedCategory = () => {
    const savedCategory = localStorage.getItem(
      `${APP_NAME}_last_selected_category`
    );
    return savedCategory || "other_kharche";
  };

  const [formData, setFormData] = useState({
    title: "",
    brand: "NA",
    weight: "NA",
    quantity: "1",
    price: "",
    description: "",
    home_id: session?.homes?.[0]?.id ?? "",
    expense_time: dayjs(),
    category: getLastSelectedCategory(),
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState({
    titles: [],
    brands: [],
    weights: [],
  });

  // Bill files state: each item = { file, status: 'uploading'|'done'|'error', name, id }
  const [billFiles, setBillFiles] = useState([]);
  const [batchToken] = useState(generateBatchToken);
  const fileInputRef = useRef(null);
  const { setRefresh } = React.useContext(FetchContext);

  // Check if any file is currently uploading
  const isUploading = useMemo(
    () => billFiles.some((f) => f.status === "uploading"),
    [billFiles]
  );

  useEffect(() => {
    const storedSuggestions = localStorage.getItem(APP_NAME + "_suggestions");
    if (storedSuggestions) {
      setSuggestions(JSON.parse(storedSuggestions));
    }
  }, []);

  // Upload a single file immediately
  const uploadSingleFile = async (file, tempId) => {
    const fd = new FormData();
    fd.append("batch_token", batchToken);
    fd.append("bill_files[]", file);

    try {
      const res = await POST_FORM(urls.post.upload_bill_images, fd);
      if (res?.success && res.uploaded_files?.length > 0) {
        // Mark as done
        setBillFiles((prev) =>
          prev.map((f) =>
            f.tempId === tempId
              ? { ...f, status: "done", serverId: res.uploaded_files[0].id }
              : f
          )
        );
      } else {
        // Mark as error and auto-remove after 2s
        const errorMsg =
          res?.errors?.[0] || res?.message || "Upload failed";
        error(errorMsg);
        setBillFiles((prev) =>
          prev.map((f) =>
            f.tempId === tempId ? { ...f, status: "error" } : f
          )
        );
        setTimeout(() => {
          setBillFiles((prev) => prev.filter((f) => f.tempId !== tempId));
        }, 2000);
      }
    } catch (err) {
      error(`Failed to upload: ${file.name}`);
      setBillFiles((prev) =>
        prev.map((f) =>
          f.tempId === tempId ? { ...f, status: "error" } : f
        )
      );
      setTimeout(() => {
        setBillFiles((prev) => prev.filter((f) => f.tempId !== tempId));
      }, 2000);
    }
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validExtensions = [
      "jpg","jpeg","png","gif","webp","bmp","svg","pdf","doc","docx","xls","xlsx",
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB

    const currentCount = billFiles.filter(
      (f) => f.status !== "error"
    ).length;

    let addedCount = 0;
    files.forEach((file) => {
      if (currentCount + addedCount >= 10) {
        error("Maximum 10 files allowed");
        return;
      }
      const ext = file.name.split(".").pop().toLowerCase();
      if (!validExtensions.includes(ext)) {
        error(`File type not allowed: ${file.name}`);
        return;
      }
      if (file.size > maxSize) {
        error(`File too large: ${file.name} (max 10MB)`);
        return;
      }

      const tempId = Date.now() + "_" + Math.random().toString(36).substring(2);
      const newFile = {
        tempId,
        name: file.name,
        status: "uploading",
        file,
        isImage: file.type.startsWith("image/"),
      };

      setBillFiles((prev) => [...prev, newFile]);
      addedCount++;

      // Start upload immediately
      uploadSingleFile(file, tempId);
    });

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (tempId) => {
    setBillFiles((prev) => prev.filter((f) => f.tempId !== tempId));
  };

  const onSubmit = (formData) => {
    setLoading(true);
    POST(urls.post.add_home_expenses, {
      ...formData,
      created_by: session?.id,
      description: `${formData.description} :: B:${formData.brand}, W:${formData.weight}, #${session?.firstname}`,
      expense_time: formData.expense_time.format("YYYY-MM-DD HH:mm:ss"),
      batch_token: billFiles.some((f) => f.status === "done")
        ? batchToken
        : "",
    }).then((res) => {
      if (res.success) {
        success(res?.message);
        localStorage.setItem(
          `${APP_NAME}_last_selected_category`,
          formData.category
        );
        setRefresh((prev) => !prev);
        props.handleClose();
      } else {
        error(res?.message);
      }
      setLoading(false);
    });
  };

  const validate = () => {
    let newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.brand.trim()) newErrors.brand = "Brand is required";
    if (!formData.weight.trim()) newErrors.weight = "Weight is required";
    if (!formData.quantity.trim() || isNaN(formData.quantity))
      newErrors.quantity = "Valid quantity is required";
    if (!formData.price.trim() || isNaN(formData.price))
      newErrors.price = "Valid price is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit(formData);
  };

  // Status icon for each file
  const getFileStatusIcon = (file) => {
    if (file.status === "uploading") {
      return (
        <CloudUploadIcon
          sx={{
            fontSize: 16,
            animation: "billBlink 1s ease-in-out infinite",
            color: "primary.main",
            "@keyframes billBlink": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.2 },
            },
          }}
        />
      );
    }
    if (file.status === "done") {
      return <CheckCircleIcon sx={{ fontSize: 16, color: "success.main" }} />;
    }
    if (file.status === "error") {
      return <ErrorIcon sx={{ fontSize: 16, color: "error.main" }} />;
    }
    return null;
  };

  const getFileIcon = (file) => {
    if (file.isImage) return <ImageIcon sx={{ fontSize: "16px !important" }} />;
    return <InsertDriveFileIcon sx={{ fontSize: "16px !important" }} />;
  };

  const uploadedCount = billFiles.filter((f) => f.status === "done").length;
  const totalFiles = billFiles.filter((f) => f.status !== "error").length;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12}>
        <FormControl fullWidth size="small">
          <InputLabel id="demo-simple-select-label">Home</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={formData.home_id}
            defaultValue={session?.homes?.[0]?.id ?? ""}
            name="home_id"
            label="Form"
            onChange={(e) =>
              setFormData({ ...formData, home_id: e.target.value })
            }
          >
            {session?.homes?.map((home) => (
              <MenuItem key={home.id} value={home.id}>
                {home.name?.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Categories Field */}
      <Grid item xs={12} sm={12}>
        <FormControl fullWidth size="small">
          <InputLabel id="category-select-label">Category</InputLabel>
          <Select
            labelId="category-select-label"
            id="category-select"
            value={formData.category}
            name="Category"
            label="Category"
            onChange={(e) => {
              const selectedCategory = e.target.value;
              setFormData({ ...formData, category: selectedCategory });
              localStorage.setItem(
                `${APP_NAME}_last_selected_category`,
                selectedCategory
              );
            }}
            defaultValue={formData.category}
          >
            {categoryOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12} sm={12}>
        <Autocomplete
          size="small"
          freeSolo
          options={suggestions.titles}
          value={formData.title}
          onInputChange={(event, newValue) =>
            setFormData({ ...formData, title: newValue })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Title"
              required
              error={!!errors.title}
              helperText={errors.title}
            />
          )}
        />
      </Grid>
      <Grid item xs={6} sm={6}>
        <TextField
          size="small"
          label="Quantity"
          type="number"
          variant="outlined"
          value={formData.quantity}
          onChange={(e) =>
            setFormData({ ...formData, quantity: e.target.value })
          }
          error={!!errors.quantity}
          helperText={errors.quantity}
          fullWidth
        />
      </Grid>

      <Grid item xs={6} sm={6}>
        <TextField
          size="small"
          label="Price"
          required
          type="number"
          variant="outlined"
          value={formData.price}
          onChange={(e) =>
            setFormData({ ...formData, price: e.target.value })
          }
          error={!!errors.price}
          helperText={errors.price}
          fullWidth
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          freeSolo
          options={suggestions.brands}
          value={formData.brand}
          onInputChange={(event, newValue) =>
            setFormData({ ...formData, brand: newValue })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Brand"
              error={!!errors.brand}
              helperText={errors.brand}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          freeSolo
          options={suggestions.weights}
          value={formData.weight}
          onInputChange={(event, newValue) =>
            setFormData({ ...formData, weight: newValue })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Weight"
              error={!!errors.weight}
              helperText={errors.weight}
            />
          )}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          size="small"
          label="Description"
          multiline
          rows={2}
          variant="outlined"
          value={formData.description}
          onChange={(e) =>
            setFormData({ ...formData, description: e.target.value })
          }
          error={!!errors.description}
          helperText={errors.description}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <LocalizationProvider dateAdapter={AdapterDayjs} sx={{ width: "100%" }}>
          <DateTimePicker
            size="small"
            sx={{ width: "100%" }}
            label="Expense Time"
            name="expense_time"
            views={["day", "month", "year"]}
            defaultValue={dayjs()}
            value={formData.expense_time}
            onChange={(val) =>
              setFormData({ ...formData, expense_time: val })
            }
            error={!!errors.expense_time}
            helperText={errors.expense_time}
          />
        </LocalizationProvider>
      </Grid>

      {/* Bill File Attachment Section */}
      <Grid item xs={12}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
          multiple
          style={{ display: "none" }}
          id="bill-file-input"
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={
            isUploading ? (
              <CloudUploadIcon
                sx={{
                  animation: "billBlink 1s ease-in-out infinite",
                  "@keyframes billBlink": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.2 },
                  },
                }}
              />
            ) : (
              <AttachFileIcon />
            )
          }
          onClick={() => fileInputRef.current?.click()}
          disabled={totalFiles >= 10 || isUploading}
          fullWidth
          sx={{
            borderStyle: "dashed",
            borderWidth: "1.5px",
            textTransform: "none",
            padding: "8px 16px",
            borderColor: isUploading
              ? "warning.main"
              : uploadedCount > 0
              ? "success.main"
              : "primary.main",
            color: isUploading
              ? "warning.main"
              : uploadedCount > 0
              ? "success.main"
              : "primary.main",
            backgroundColor: isUploading
              ? "rgba(237, 108, 2, 0.04)"
              : uploadedCount > 0
              ? "rgba(46, 125, 50, 0.04)"
              : "transparent",
            "&:hover": {
              borderStyle: "dashed",
              borderWidth: "1.5px",
            },
          }}
        >
          {isUploading
            ? "Uploading..."
            : uploadedCount > 0
            ? `${uploadedCount} file${uploadedCount > 1 ? "s" : ""} attached ✓`
            : "📎 Attach Bills (images, pdf, doc)"}
        </Button>

        {/* File chips with status */}
        {billFiles.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
              mt: 1,
            }}
          >
            {billFiles.map((file) => (
              <Chip
                key={file.tempId}
                icon={getFileStatusIcon(file)}
                label={
                  file.name.length > 15
                    ? file.name.substring(0, 12) + "..."
                    : file.name
                }
                size="small"
                onDelete={
                  file.status === "uploading"
                    ? undefined
                    : () => removeFile(file.tempId)
                }
                deleteIcon={
                  <CloseIcon sx={{ fontSize: "14px !important" }} />
                }
                variant="outlined"
                color={
                  file.status === "done"
                    ? "success"
                    : file.status === "error"
                    ? "error"
                    : "primary"
                }
                sx={{
                  maxWidth: "100%",
                  "& .MuiChip-label": { fontSize: "0.7rem" },
                  ...(file.status === "uploading" && {
                    animation: "billBlink 1.2s ease-in-out infinite",
                    "@keyframes billBlink": {
                      "0%, 100%": { opacity: 1 },
                      "50%": { opacity: 0.3 },
                    },
                  }),
                }}
              />
            ))}
          </Box>
        )}
      </Grid>

      <Grid
        item
        xs={12}
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
      >
        <Button
          onClick={handleClose}
          variant="contained"
          color="error"
          sx={{ marginRight: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          disabled={loading || isUploading}
        >
          {loading ? (
            <CircularProgress size={20} />
          ) : isUploading ? (
            "Uploading..."
          ) : (
            "Submit"
          )}
        </Button>
      </Grid>
    </Grid>
  );
};

export default DI(HomeExpenseForm);